// types.ts

export interface VariableValue {
  value: string;
  correlations?: Record<string, string>; // Maps to other variable IDs
}

export interface ScriptVariable {
  name: string;
  values: VariableValue[]; // Support both new and legacy formats
}

export interface VariableGroup {
  name: string;
  variables: ScriptVariable[];
}

export interface Script {
  _id: string;
  name: string;
  headline: string;
  body: string;
  cta: string;
  variables?: Variable[];
  variableGroups?: VariableGroup[];
}

export interface Variable {
  name: string;
  values: string[]
}

export interface GeneratedVideo {
  _id: string;
  actor_id: string;
  actorName: string;
  voiceName: string;
  voice: string;
  heygenId: string;
  ttsAudioUrl?: string;
  video_url?: string; // Updated field name from 'url'
  video_url_caption?:string;
  caption_url?: string;
  withCaptions?: boolean;
  captionStyle?: string; // Preset for caption styling (e.g. 'default', 'classic', 'kinetic')
  captionExportType?: 'baked_in' | 'separate' | 'both'; // both = baked-in video + separate subtitle file
  styledCaptionsMetadata?: {
    styledSegments?: Array<{ startSeconds: number; endSeconds: number; text: string }>;
    presetUsed?: string;
    fromApi?: boolean;
  };
  green_screen_caption_url?: string; // Layer 2: captions on green screen background
  styled_caption_url?: string;       // Layer 3: styled captions baked into original video
  // Submagic caption engine
  submagicProjectId?: string;
  submagicStatus?: string;    // processing | transcribing | exporting | completed | failed
  submagicTemplate?: string;
  submagicDownloadUrl?: string;
  submagicDirectUrl?: string;
  gif_url?: string; // New field for gif URL
  scriptId?: string; // New field for script ID
  thumbnail_url?: string; // New field for thumbnail URL
  duration?: number; // New field for video duration
  status: string;
  dimension: {
    width: number;
    height: number;
  };
  resolution?: '4k' | '1080p' | '720p';
  aspect_ratio?: '16:9' | '9:16';
  generatedBy?: User | string; // Can be populated or just the ID
  name?: string;
  googleDriveUrl?: string;
  videoAgent?: boolean;
}

export interface VideoAgentSessionMessage {
  role: 'user' | 'model';
  content: string;
  type: 'text' | 'resource' | 'error';
  resource_ids?: string[];
  created_at: number;
}

export interface VideoAgentSessionVideo {
  heygenVideoId: string;
  generatedVideo: GeneratedVideo;
  addedAt: string;
}

export interface VideoAgentStyle {
  style_id: string;
  name: string;
  thumbnail_url?: string;
  preview_video_url?: string;
  tags?: string[];
  aspect_ratio?: string;
}

export interface VideoAgentSession {
  _id: string;
  heygenSessionId: string;
  mode: 'generate' | 'chat';
  status: 'thinking' | 'waiting_for_input' | 'reviewing' | 'generating' | 'completed' | 'failed';
  progress: number;
  title: string | null;
  prompt: string;
  messages: VideoAgentSessionMessage[];
  videos: VideoAgentSessionVideo[];
  workspace: string;
  generatedBy: User | string;
  lastSyncedAt: string;
}

export interface IVAvatarVideo {
  _id: string;
  image_key: string;
  video_title: string;
  script: string;
  voice_id: string;
  video_orientation: 'portrait' | 'landscape';
  fit: 'cover' | 'contain';
  custom_motion_prompt?: string;
  enhance_custom_motion_prompt: boolean;
  audio_url?: string;
  audio_asset_id?: string;
  heygenId: string;
  status: 'processing' | 'completed' | 'failed';
  video_url?: string;
  thumbnail_url?: string;
  duration?: number;
  workspace: string | Workspace;
  generatedBy: string | User;
  videoPage?: string | VideoPage;
  isTest: boolean;
  dimension: {
    width: number;
    height: number;
  };
  googleDriveUrl?: string;
  name?: string;
  createdAt: string;
  updatedAt: string;
}

export type AnyVideo = GeneratedVideo | IVAvatarVideo;

// Avatar Group Types
export interface AvatarGroup {
  _id: string;
  group_id: string;
  name: string;
  image_url?: string;
  image_key?: string;
  generation_id?: string;
  status: 'pending' | 'training' | 'ready' | 'failed';
  training_status: 'pending' | 'training' | 'ready' | 'failed';
  training_error_msg?: string;
  training_created_at?: string;
  training_updated_at?: string;
  is_motion: boolean;
  motion_preview_url?: string;
  business_type: 'generated' | 'uploaded';
  upscale_availability: {
    available: boolean;
    reason?: string;
  };
  upscaled: boolean;
  background_sound_effect?: string;
  default_voice_id?: string;
  moderation_msg?: string;
  base_avatar: CustomPhotoAvatar;
  workspace: string | Workspace;
  organization?: string | Organization;
  createdBy: string | User;
  looks: CustomPhotoAvatar[];
  isTest: boolean;
  error_message?: string;
  createdAt: string;
  updatedAt: string;
}

// Look Generation Request Type
export interface LookGenerationRequest {
  workspaceId: string;
  userEmail: string;
  group_id: string;
  prompt: string;
  orientation?: 'square' | 'horizontal' | 'vertical';
  pose?: 'half_body' | 'close_up' | 'full_body';
  style?: 'Realistic' | 'Pixar' | 'Cinematic' | 'Vintage' | 'Noir' | 'Cyberpunk' | 'Unspecified';
  name?: string;
}

// Avatar Group Creation Request Type
export interface AvatarGroupCreationRequest {
  workspaceId: string;
  userEmail: string;
  base_avatar_id: string;
  name?: string;
}

// Look Generation Response Type
export interface LookGenerationResponse {
  generationId: string;
  lookAvatarId: string;
  avatarGroupId: string;
  message: string;
}

// Avatar Group Creation Response Type
export interface AvatarGroupCreationResponse {
  avatarGroupId: string;
  groupId: string;
  message: string;
}


// Motion type enum
export type MotionType = 'expressive' | 'consistent' | 'consistent_gen_3' | 'hailuo_2' | 'veo2' | 'seedance_lite' | 'kling';

// Background sound effect status
export interface BackgroundSoundEffect {
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  sound_effect_id?: string;
  sound_effect_preview_url?: string;
  file_meta?: any;
}

// Custom Photo Avatar Interface
export type CustomPhotoAvatarApiVersion = 'v2' | 'v3';
export type CustomPhotoAvatarType = 'photo' | 'digital_twin' | 'prompt';
export type CustomPhotoAvatarLookType = 'studio_avatar' | 'digital_twin' | 'photo_avatar';

export interface CustomPhotoAvatar {
  _id: string;
  name: string;
  age: 'Young Adult' | 'Early Middle Age' | 'Late Middle Age' | 'Senior' | 'Unspecified';
  gender: 'female' | 'male' | 'Unspecified';
  ethnicity: string;
  orientation: 'square' | 'horizontal' | 'vertical';
  pose: 'half_body' | 'close_up' | 'full_body';
  style: 'Realistic' | 'Pixar' | 'Cinematic' | 'Vintage' | 'Noir' | 'Cyberpunk' | 'Unspecified';
  appearance?: string;
  // V2 records have generation_id; V3 records do not
  generation_id?: string;
  photo_avatar_id?: string;
  status: 'processing' | 'success' | 'failed' | 'pending_consent' | 'completed';
  image_url_list: string[];
  image_key_list: string[];
  selected_image_url?: string;
  selected_image_key?: string;
  callback_url?: string;
  callback_id?: string;

  // V3 discriminator + look-level fields (populated when apiVersion === 'v3')
  apiVersion?: CustomPhotoAvatarApiVersion;
  avatarType?: CustomPhotoAvatarType;
  avatar_look_id?: string;
  avatar_look_type?: CustomPhotoAvatarLookType;
  preview_image_url?: string | null;
  preview_video_url?: string | null;
  default_voice_id?: string | null;
  supported_api_engines?: string[];
  image_width?: number | null;
  image_height?: number | null;
  preferred_orientation?: 'portrait' | 'landscape' | 'square' | null;
  look_tags?: string[];
  voice_settings?: TextVoiceSettings | AudioVoiceSettings | SilenceVoiceSettings;
  heygenVoiceId?: string;
  heygenVoiceAudioUrl?: string;
  elevenLabsVoiceId?: string;
  voiceName?: string;
  voiceConfiguration?: {
    stability: number;
    similarity_boost: number;
    style: number;
    use_speaker_boost: boolean;
    speed: number;
  };
  elevenLabsAudioUrl?: string;
  workspace: string | Workspace;
  createdBy: string | User;
  videoPage?: string | VideoPage;
  caption: boolean;
  title?: string;
  organization?: Organization;
  isTest: boolean;
  resolution?: '4k' | '1080p' | '720p';
  aspect_ratio?: '16:9' | '9:16';
  error_message?: string;
  // avatar groups
  avatar_group?: string | AvatarGroup;
  group_id?: string;
  is_base_avatar: boolean;
  // Motion fields
  is_motion?: boolean;
  motion_preview_url?: string;
  motion_type?: MotionType;
  motion_prompt?: string;
  // Upscale availability
  upscale_availability?: {
    available: boolean;
    reason?: string;
  };
  upscaled?: boolean;
  // Background sound effect
  background_sound_effect?: BackgroundSoundEffect;
  // Additional fields from HeyGen
  business_type?: string;
  moderation_msg?: string;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// CustomPhotoAvatar V3 (HeyGen V3 endpoints)
// ============================================

export interface CreateCustomPhotoAvatarV3UploadResponse {
  customPhotoAvatarId: string;
  avatar: CustomPhotoAvatar;
  message: string;
}

export interface CreateCustomPhotoAvatarV3PromptRequest {
  workspaceId: string;
  userEmail: string;
  name: string;
  prompt: string;
  videoPageId?: string;
  // Reference images are ONLY allowed when avatarGroupId is set
  referenceImages?: Array<
    | { type: 'url'; url: string }
    | { type: 'asset_id'; asset_id: string }
  >;
  avatarGroupId?: string;
}

export interface UpdateAvatarLookV3Response {
  data: AvatarLookItem;
  avatar: CustomPhotoAvatar | null;
  message: string;
}

export interface AvatarLookItem {
  id: string;
  name: string;
  type?: CustomPhotoAvatarLookType;
  preview_image_url?: string | null;
  preview_video_url?: string | null;
  default_voice_id?: string | null;
  supported_api_engines?: string[];
  status?: string;
}

export interface AvatarGroupItem {
  id: string;
  name: string;
  preview_image_url: string | null;
  preview_video_url: string | null;
  gender: 'female' | 'male' | 'unknown' | null;
  created_at: number;
  looks_count: number;
  default_voice_id: string | null;
  consent_status: string;
  status: string;
}

export interface ListAvatarGroupsV3Response {
  data: AvatarGroupItem[];
  has_more: boolean;
  next_token?: string;
}

export interface GetAvatarGroupV3Response {
  data: AvatarGroupItem;
}

export interface VideoPage {
  _id: string;
  name: string;
  script: Script[];
  generatedVideos: GeneratedVideo[];
  ivAvatarVideos: IVAvatarVideo[];
  updatedAt: string;
  actors?: Actor[];
  ivAvatars?: CustomPhotoAvatar[];
  assetActors?: AssetActor[];
  createdBy: User;
  lastUpdatedBy: string[];
  speechAudioUrl?: string;
  creativeRequest?: CreativeRequest;
}

export interface AssetActor {
  _id: string;
  name: string;
  // Image source (mutually exclusive)
  avatar_id?: string;
  image_url?: string;
  image_asset_id?: string;
  // Voice
  voice_id?: string;
  voiceName?: string;
  voice_settings?: {
    speed?: number;
    pitch?: number;
    locale?: string;
  };
  // Audio (alternative to voice)
  audio_url?: string;
  audio_asset_id?: string;
  // Video defaults
  resolution?: '4k' | '1080p' | '720p';
  aspect_ratio?: '16:9' | '9:16';
  motion_prompt?: string;
  expressiveness?: 'low' | 'medium' | 'high';
  remove_background?: boolean;
  background?: {
    type: 'color' | 'image';
    value?: string;
    url?: string;
    asset_id?: string;
  };
  // References
  workspace: string;
  videoPage: string;
  createdBy: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Asset {
  _id: string;
  assetId: string; // Heygen's asset ID or S3 key
  url: string; // URL of the uploaded asset
  type: 'image' | 'video';
  source: 'heygen' | 's3';
  userId: string | User; // Can be populated or just the ID
  organizationId?: string; // Organization ID
  workspaceId: string | Workspace; // Can be populated or just the ID
  projectId?: string | Project; // Optional, can be populated or just the ID
  videoPageId?: string | VideoPage; // Optional, can be populated or just the ID
  imageKey?: string; // Optional key for image storage (only for heygen source)
  name?: string; // Optional name for the asset
  // Background settings fields
  fit?: 'cover' | 'crop' | 'contain' | 'none'; // How the background fits the scene
  playStyle?: 'fit_to_scene' | 'freeze' | 'loop' | 'once'; // Only for video backgrounds
  promptToMedia?: boolean;
  createdAt: Date;
}

export interface CreativeRequest {
  _id: string;
  creativeType: 'video' | 'static';
  headerType: string;
  type: keyof typeof CreativeRequestType;
  brand: string;
  brandDescription: string;
  adDescription: string;
  country: string;
  hooks: string[];
  bodies: string[];
  ctas: string[];
  notes?: string;
  priority: 'low' | 'medium' | 'high';
  dateAssigned: string;
  requester: User; // User ID
  receiver?: User; // User ID
  creativesWorkspace: string; // Workspace ID
}

export enum CreativeRequestType {
  AU = 'AI UGC',
  RE = 'Reaction',
  RG = 'Reaction + Gameplay',
  GP = 'Gameplay',
  UG = 'Real UGC / Explainer',
  PV = 'Promo Video',
  AS = 'Animated Stills',
  SK = 'UGC Skits [Multiple People] / Slice of Life',
}

export interface Project {
  _id: string;
  name: string;
  videoPages: VideoPage[];
}

export interface VoiceSettings2 {
  stability: number;
  clarity: number;
  style: number;
  speed: number;
}

export interface Voice {
  _id: string;
  voiceSettings: VoiceSettings;
  externalId: string;
  name: string;
  gender: 'male' | 'female';
  language: string;
  locale: string;
  preview: string;
}

export type ElevenLabsModel = 
  | 'eleven_monolingual_v1' 
  | 'eleven_multilingual_v1' 
  | 'eleven_multilingual_v2' 
  | 'eleven_turbo_v2' 
  | 'eleven_turbo_v2_5';

export interface ElevenLabsSettings {
  model: ElevenLabsModel;
  similarity_boost: number; // 0.0 to 1.0
  stability: number; // 0.0 to 1.0
  style: number; // 0.0 to 1.0
}

// Voice Emotion Types
export type VoiceEmotion = 
  | 'Excited' 
  | 'Friendly' 
  | 'Serious' 
  | 'Soothing' 
  | 'Broadcaster';


// Voice Setting Base Interface
interface BaseVoiceSettings {
  type: string;
  free?: {
    pitch: number;
    speed: number;
    text: string;
    tts_audio_url: string;
    tts_duration: number;
    voice_gender: string;
    voice_id: string;
    voice_language: string;
  };
  premium?: {
    pitch: number;
    speed: number;
    text: string;
    tts_audio_url: string;
    tts_duration: number;
    voice_gender: string;
    voice_id: string;
    voice_language: string;
  };
}

// Voice Setting Types
export interface TextVoiceSettings extends BaseVoiceSettings {
  type: 'text';
  voice_id: string;
  input_text: string;
  speed?: number; // 0.5 to 1.5, default 1
  pitch?: number; // -50 to 50, default 0
  emotion?: VoiceEmotion;
  locale?: string; // e.g. 'en-US', 'en-IN'
  elevenlabs_settings?: ElevenLabsSettings;
}

export interface AudioVoiceSettings extends BaseVoiceSettings {
  type: 'audio';
  audio_url?: string; // Either url or asset_id required
  audio_asset_id?: string;
}

export interface SilenceVoiceSettings extends BaseVoiceSettings {
  type: 'silence';
  duration: number; // 1.0 to 100.0, default 1.0
}



// Main Actor Interface
export interface Actor {
  _id: string;
  avatar_id: string;
  isTemplateActor?: boolean;
  avatar_name: string;
  avatar_states: AvatarState[];
  created_at?: number;
  gender: 'male' | 'female' | 'unknown';
  name: string;
  preview_image_url: string;
  preview_video_url: string;
  
  // Voice configuration
  voice_settings: VoiceSettings;
  voiceName?: string;
  
  // Workspace references
  workspace: string;
  videoPage: string;
  
  // Talking photo configuration
  isTalkingPhoto?: boolean;
  talking_photo_id?: string;
  talking_photo_name?: string;
  
  // Visual configuration
  avatarScale?: number;
  backgroundAsset?: Asset;
  avatarOffset?: { x: number; y: number };
  
  // Video options
  caption?: boolean;
  title?: string;
  resolution?: '4k' | '1080p' | '720p';
  aspect_ratio?: '16:9' | '9:16';

  // Legacy fields (for backward compatibility)
  elevenLabsVoiceId?: string;
  heygenVoiceId?: string;
  heygenVoiceAudioUrl?: string;
  elevenLabsAudioUrl?: string;
  voiceConfiguration?: {
    stability?: number;
    similarity_boost?: number;
    style?: number;
    use_speaker_boost?: boolean;
    speed?: number;
  };
  heygenTemplate?: HeygenTemplate;
}

export interface HeygenTemplate {
  template_id: string;
  name: string;
  title?: string;
  thumbnail_image_url?: string;
  aspect_ratio?: 'portrait' | 'landscape';
  variables?: Record<string, TemplateVariable>;
}

export interface TemplateVariable {
  name?: string;
  type?: string;
  properties?: {
    content?: string;
    url?: string;
    asset_id?: string;
    fit?: string;
    play_style?: string;
    character_id?: string;
    type?: string;
    voice_id?: string;
  };
}

// Helper type for creating new actors
export interface NewActor {
  avatar_id: string;
  workspace: string;
  videoPage: string;
  voice_settings: VoiceSettings;
  name?: string;
  isTalkingPhoto?: boolean;
  // Other optional fields
}

// Type guard functions for voice settings
export function isTextVoiceSettings(settings: TextVoiceSettings) {
  return settings.type === 'text';
}

export function isAudioVoiceSettings(settings: AudioVoiceSettings) {
  return settings.type === 'audio';
}

export function isSilenceVoiceSettings(settings: SilenceVoiceSettings) {
  return settings.type === 'silence';
}

// Default voice settings factories
export const defaultTextVoiceSettings = (voiceId: string, text: string): TextVoiceSettings => ({
  type: 'text',
  voice_id: voiceId,
  input_text: text,
  speed: 1,
  pitch: 0
});

export const defaultAudioVoiceSettings = (audioUrl: string): AudioVoiceSettings => ({
  type: 'audio',
  audio_url: audioUrl
});

export const defaultSilenceVoiceSettings = (duration: number = 1.0): SilenceVoiceSettings => ({
  type: 'silence',
  duration
});

export interface ActorUpdateSettings {
  avatarScale?: number;
  backgroundAssetId?: string;
  avatarOffset?: { x: number; y: number };
  backgroundFit?: string;
  backgroundPlayStyle?: string;
  caption?: boolean;
  title?: string;
  voiceConfiguration?: {
    stability?: number;
    similarity_boost?: number;
    style?: number;
    use_speaker_boost?: boolean;
    speed?: number;
  };
}

export interface AvatarState {
  id: string; // Required
  acl?: AccessControl[];
  available_style?: AvailableStyle;
  avatar_name?: string;
  avatar_race?: string;
  avatar_style?: string;
  circle_box?: number[];
  close_up_box?: number[];
  close_up_view_crop_x?: number;
  created_at?: number;
  custom_avatar_type?: string;
  default_ac?: DefaultAccessControl;
  default_voice?: VoiceSettings;
  end_speech_offset?: number;
  for_url2video?: boolean;
  gender?: 'male' | 'female' | 'unknown';
  instant_avatar_video_data?: InstantAvatarVideoData;
  is_customer?: boolean;
  is_demo?: boolean;
  is_expressive?: boolean;
  is_favorite?: boolean;
  is_instant_avatar?: boolean;
  is_new?: boolean;
  is_old_pose?: boolean;
  is_open_to_all?: boolean;
  is_paid?: boolean;
  is_preset?: boolean;
  normal_preview?: string;
  normal_preview_height?: number;
  normal_preview_width?: number;
  normal_thumbnail_medium?: string;
  normal_thumbnail_small?: string;
  normal_view_crop_x?: number;
  pose_name?: string;
  premium?: boolean;
  preview_key?: string;
  redo_left?: number;
  sort_index?: number;
  start_speech_offset?: number;
  status?: 'ACTIVE' | 'INACTIVE';
  support_4k?: boolean;
  support_eye_contact?: boolean;
  support_matting?: boolean;
  supported_templates?: string[];
  tags?: string[];
  unboxed?: boolean;
  video_url?: VideoUrl;
}
interface AccessControl {
  access_type: number;
  username: string;
}

interface AvailableStyle {
  circle: boolean;
  close_up: boolean;
  normal: boolean;
}

interface DefaultAccessControl {
  access_type: number;
  overwrite: boolean;
}

export type VoiceSettings = 
  | TextVoiceSettings 
  | AudioVoiceSettings 
  | SilenceVoiceSettings;

interface VoiceDetails {
  pitch: number;
  speed: number;
  text: string;
  tts_audio_url?: string;
  tts_duration?: number;
  voice_gender: string;
  voice_id: string;
  voice_language: string;
}


interface InstantAvatarVideoData {
  consent_thumbnail_url: string;
  consent_video_key: string;
  consent_video_url: string;
  contact_email: string;
  created_at: number;
  creator_username: string;
  eta: number;
  gender: 'male' | 'female' | 'unknown';
  group_id: string;
  id: string;
  language: string;
  metadata: Metadata;
  moderation_status: string;
  msg: string;
  pose_name: string;
  progress: number;
  signature: string;
  slot_index: number;
  status: string;
  thumbnail_url: string;
  updated_at: number;
  video_key: string;
  video_url: string;
}

interface Metadata {
  avatar_style: string;
  consent_md5_sum: string;
  consent_sync_score: number;
  consent_video_dict: VideoDict;
  footage_sync_score: number;
  has_finetuned: boolean;
  height: number;
  orientation: string;
  preview_audio_key: string;
  resized_consent_video_key: string;
  resized_video_key: string;
  upload_type: string;
  user_avatar_id: string;
  user_voice_clone_id: string;
  video_dict: VideoDict;
  width: number;
  thumbnail_url: string;
}

interface VideoDict {
  source_mode: string;
  thumbnail_key: string;
  video_key: string;
}

interface VideoUrl {
  grey: string;
}

// AUTH
export interface User {
  _id: string;
  name: string;
  email: string;
  permissions: string[]; // Array of permissions
  ownedWorkspaces: Workspace[]; // Array of workspace IDs the user owns
  memberWorkspaces: Workspace[]; // Array of workspace IDs the user is a member of
  adminWorkspaces: Workspace[]; // Array of workspace IDs the user is an admin of
  role: 'admin' | 'user' | 'limited';
  videoGenerations: VideoGeneration[]; // Added videoGenerations
  organization: Organization;
  fullAccessDueDate?: Date | null;
  isAppAdmin?: boolean;
  googleDriveAccess?: boolean;
  notifications?: string[];
  googleDriveTokens?: {
    access_token?: string;
    refresh_token?: string;
    expiry_date?: number;
    scope?: string;
  };
  googleId?: string;
  isGoogleUser?: boolean;
  isVerified?: boolean;
  zapflowAccess?: boolean;
  farmSystemAccess?: boolean;
  farmSystemFinance?: boolean;
}

export interface VideoGeneration {
  workspaceId: string;
  numberOfGeneratedVideos: number;
}

export interface Invitation {
  key: string;
  expirationDate: Date;
  role: 'admin' | 'member' | 'limited';
}

export interface Workspace {
  _id: string;
  name: string;
  members: User[]; // Array of user IDs
  admins: User[]; // Array of user IDs
  projects: Project[]; // Array of project IDs
  owner: User; // User ID of the owner
  invitations: Invitation[]; // Updated from invitation to invitations
  category: 'fullaccess' | 'limited';
  plan?: Plan; // Can be populated or just the ID
  generatedVideos: number;
  generatedTestVideos?: number;
  // Per-engine monthly counters (admin "current usage" display)
  monthlyGeneratedVideos?: number;
  monthlyVideoAgentVideos?: number;
  monthlyPromptToVideoVideos?: number;
  // Live count of active CustomPhotoAvatar docs in the workspace (admin only)
  avatarCount?: number;
  sora2Media?: string[];
  isTrial?: boolean;
  trialCredits?: number;
  trialCreditsUsed?: number;
  trialCreditsVideoAgent?: number;
  trialCreditsVideoAgentUsed?: number;
  trialCreditsPromptToVideo?: number;
  trialCreditsPromptToVideoUsed?: number;
  feedbackLoopAccess?: boolean;
}

export type FeedbackLoopEngine = 'seedance' | 'veo3' | 'sora2' | 'nano-banana' | 'heygen';
export type FeedbackLoopStatus = 'processing' | 'success' | 'fail';
export interface FeedbackLoopMedia {
  _id: string; engine: FeedbackLoopEngine; model: string;
  mediaType: 'video' | 'image'; prompt: string;
  mediaUrl: string | null; thumbnailUrl?: string | null;
  status: FeedbackLoopStatus; approved: boolean | null;
  workspaceId: string; createdAt: string;
}

export interface Organization {
  _id: string;
  name: string;
  organizationKey: string;
  letsLaunchAccess: boolean;
}

export interface ElevenLabsVoice  {
    voice_id: string;
    name: string;
    preview_url: string;
    settings: {
        stability: number;
        similarity_boost: number;
        style: number;
        use_speaker_boost: boolean;
        speed?: number;
    };
};

export interface HeygenVoice {
  voice_id: string;
  name: string;
  language: string;
  gender: 'male' | 'female';
  preview_audio_url: string | null;
  support_pause: boolean;
  support_locale: boolean;
  type: 'public' | 'private';
}

export interface CustomVoice {
  _id: string;
  generationId: string;
  voice_id: string;
  name: string;
  language: string;
  gender: string;
  preview_audio_url: string | null;
  support_pause: boolean;
  support_locale: boolean;
  type: 'public' | 'private';
  prompt: string;
  locale: string | null;
  seed: number;
  userId: string;
  workspace: string;
  createdAt: string;
  updatedAt: string;
}


export interface Plan {
  _id: string;
  name: 'starter' | 'pro' | 'custom';
  videoLimit: number;
  seats: number;
  features: Array<
    'ADVANCED_AVATARS' |
    'CUSTOM_VARIABLES' |
    'MULTIPLE_GENERATION' |
    'ELEVEN_LABS' |
    'ELEVEN_LABS_OWN_KEY' |
    'GPT_SCRIPT_GENERATION' |
    'CSV_UPLOAD' |
    'VIDEO_DURATION_2'
  >;
  pricePerUGC: number;
  isCustom: boolean;
  // Per-engine monthly/total limit overrides. `undefined` = use the
  // canonical default for the plan tier (see src/constants/limits.ts).
  heygenMonthlyLimit?: number;
  videoAgentMonthlyLimit?: number;
  promptToVideoMonthlyLimit?: number;
  avatarTotalLimit?: number;
}

// Add admin-specific types
export interface AdminStats {
  totalUsers: number;
  premiumUsers: number;
  plans: {
    starter: number;
    pro: number;
    custom: number;
    total: number;
  };
  heygenVideos: { thisMonth: number; lastMonth: number };
  ivAvatarVideos: { thisMonth: number; lastMonth: number };
  videoAgentVideos: { thisMonth: number; lastMonth: number };
  sora2Videos: {
    thisMonth: number;
    lastMonth: number;
    byVersion: {
      standard: { thisMonth: number; lastMonth: number };
      pro: { thisMonth: number; lastMonth: number };
      storyboard: { thisMonth: number; lastMonth: number };
    };
  };
  seedanceVideos: {
    thisMonth: number;
    lastMonth: number;
    byVersion: {
      v1: { thisMonth: number; lastMonth: number };
      v15Pro: { thisMonth: number; lastMonth: number };
      v2: { thisMonth: number; lastMonth: number };
      v2Fast: { thisMonth: number; lastMonth: number };
    };
  };
  veo3Videos: {
    thisMonth: number;
    lastMonth: number;
    byVersion: {
      fast: { thisMonth: number; lastMonth: number };
      quality: { thisMonth: number; lastMonth: number };
    };
  };
  kieApiCost: {
    thisMonth: { credits: number; costUSD: number };
    lastMonth: { credits: number; costUSD: number };
  };
  subscriptionRevenue: {
    thisMonth: { monthly: number; usage: number; selected: number };
    lastMonth: { monthly: number; usage: number; selected: number };
  };
}

export interface AdminUserFilters {
  search?: string;
  premium?: boolean;
  organization?: string;
  role?: string;
}

export interface AdminWorkspaceFilters {
  search?: string;
  hasPlan?: boolean;
  isTrial?: boolean;
  organization?: string;
}

export interface BackgroundSettings {
  scale: number;
  backgroundAssetId?: string;
  selectedDimension?: { width: number; height: number };
  selectedAvatarOffset?: { x: number; y: number };
  backgroundFit?: 'cover' | 'crop' | 'contain' | 'none';
  backgroundPlayStyle?: 'fit_to_scene' | 'freeze' | 'loop' | 'once';
};


export interface VoiceLocale {
  value: string;      // e.g. "en-US"
  label: string;      // e.g. "English (US)"
  language: string;   // e.g. "English"
  languageCode: string; // e.g. "en-US"
}

export interface TemplateGuidelines {
  template_id: string;
  title?: string;
  description?: string;
  videoExample?: string;
  imageExample?: string;
}

// CONSTS
export const MAX_VIDEOS_PER_FREE_USER=3


// REQUESTS
export interface GenerateIVAvatarVideoRequest {
  workspaceId: string;
  videoPageId?: string;
  userEmail: string;
  image_key: string;
  video_title: string;
  script: string;
  voice_id: string;
  video_orientation?: 'portrait' | 'landscape';
  fit?: 'cover' | 'contain';
  custom_motion_prompt?: string;
  enhance_custom_motion_prompt?: boolean;
  audio_url?: string;
  audio_asset_id?: string;
  dimension?: { width: number; height: number };
  resolution?: '4k' | '1080p' | '720p';
  aspect_ratio?: '16:9' | '9:16';
  // V3 routing: backend uses avatar_look_id as avatar_id for POST /v3/videos
  avatar_look_id?: string;
  apiVersion?: CustomPhotoAvatarApiVersion;
  test?: boolean;
  // Eleven Labs specific fields
  isSpeech?: boolean;
  useElevenLabs?: boolean;
  elevenLabsVoiceId?: string;
  elevenLabsVoiceSettings?: {
    stability: number;
    similarity_boost: number;
    style: number;
    use_speaker_boost: boolean;
    speed: number;
  };
}
export interface GenerateCustomPhotoAvatarRequest {
  workspaceId: string;
  videoPageId?: string;
  userEmail: string;
  name: string;
  age?: 'Young Adult' | 'Early Middle Age' | 'Late Middle Age' | 'Senior' | 'Unspecified';
  gender?: 'Woman' | 'Man' | 'Unspecified';
  ethnicity?: string;
  orientation?: 'square' | 'horizontal' | 'vertical';
  pose?: 'half_body' | 'close_up' | 'full_body';
  style?: 'Realistic' | 'Pixar' | 'Cinematic' | 'Vintage' | 'Noir' | 'Cyberpunk' | 'Unspecified';
  appearance?: string;
  callback_url?: string;
  callback_id?: string;
  organizationId: string;
}

export interface SelectPhotoAvatarImageRequest {
  imageUrl: string;
  imageKey: string;
}

export interface UpdateCustomPhotoAvatarVoiceSettingsRequest {
  voice_settings?: TextVoiceSettings | AudioVoiceSettings | SilenceVoiceSettings;
  heygenVoiceId?: string;
  heygenVoiceAudioUrl?: string;
  elevenLabsVoiceId?: string;
  voiceName?: string;
  voiceConfiguration?: {
    stability?: number;
    similarity_boost?: number;
    style?: number;
    use_speaker_boost?: boolean;
    speed?: number;
  };
}

export interface SelectedAvatarLabActor {
  _id: string;
  avatar_id: string;
  avatar_name: string;
  selected_image_key: string;
  selected_image_url: string;
  isCustomPhotoAvatar: true;
}

// Sora2 Media Generation Types
export type Sora2GenerationType = 'text-to-video' | 'image-to-video' | 'storyboard' | 'pro-text-to-video' | 'pro-image-to-video';
export type Sora2Size = 'standard' | 'high';
export type Sora2AspectRatio = 'portrait' | 'landscape';
export type Sora2Frames = '10' | '15' | '25';

export interface Sora2Shot {
  Scene: string;
  duration: number;
}
export type Sora2State = 'pending' | 'processing' | 'success' | 'fail';

export interface Sora2Media {
  _id: string;
  taskId: string;
  generationType: Sora2GenerationType;
  model: string;
  prompt: string;
  imageUrls: string[];
  aspectRatio: Sora2AspectRatio;
  nFrames: Sora2Frames;
  shots?: Sora2Shot[];
  size?: Sora2Size | null;
  uploadMethod: 's3' | 'oss';
  state: Sora2State;
  resultUrls: string[];
  resultWaterMarkUrls?: string[];
  createTime?: string;
  completeTime?: string;
  costTime?: number;
  failCode?: string;
  failMsg?: string;
  workspaceId: string;
  userId?: string;
  // Watermark fields
  watermarkRemoved?: boolean;
  watermarkRemovalTaskId?: string;
  watermarkRemovalStatus?: 'pending' | 'processing' | 'success' | 'fail';
  watermarkRemovedResultUrls?: string[];
  assetUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTextToVideoRequest {
  prompt: string;
  aspectRatio?: Sora2AspectRatio;
  nFrames?: Sora2Frames;
  workspaceId: string;
  userId?: string;
}

export interface CreateImageToVideoRequest {
  prompt: string;
  imageUrls: string[];
  aspectRatio?: Sora2AspectRatio;
  nFrames?: Sora2Frames;
  workspaceId: string;
  userId?: string;
}

export interface CreateProTextToVideoRequest {
  prompt: string;
  imageUrls?: string[];
  aspectRatio?: Sora2AspectRatio;
  nFrames?: Sora2Frames;
  size?: Sora2Size;
  workspaceId: string;
  userId?: string;
}

export interface CreateProImageToVideoRequest {
  prompt: string;
  imageUrls: string[];
  aspectRatio?: Sora2AspectRatio;
  nFrames?: Sora2Frames;
  size?: Sora2Size;
  workspaceId: string;
  userId?: string;
}

export interface CreateStoryboardRequest {
  shots: Sora2Shot[];
  imageUrls?: string[];
  aspectRatio?: Sora2AspectRatio;
  nFrames?: Sora2Frames;
  workspaceId: string;
  userId?: string;
}

export interface Sora2Response {
  success: boolean;
  message: string;
  data: {
    taskId: string;
    sora2Media: Sora2Media;
  };
}

export interface Sora2TaskResponse {
  success: boolean;
  data: Sora2Media;
}

export interface Sora2TasksResponse {
  success: boolean;
  data: Sora2Media[];
}

export interface SaveToAssetsRequest {
  taskId: string;
  workspaceId: string;
  userId?: string;
  organizationId?: string;
  name?: string;
}

export interface SaveToAssetsResponse {
  success: boolean;
  message: string;
  data: Asset[];
}

// ============================================
// Seedance (ByteDance Seedance V1 Pro) Types
// ============================================

export type SeedanceGenerationType = 'text-to-video' | 'image-to-video' | '1.5-pro' | '2.0' | '2.0-fast' | '2.5';
// Seedance 2.0 adds 'adaptive'
export type SeedanceAspectRatio = '21:9' | '16:9' | '4:3' | '1:1' | '3:4' | '9:16' | 'adaptive';
export type SeedanceOutputFormat = 'mp4' | 'mov';
export type SeedanceResolution = '480p' | '720p' | '1080p';
export type SeedanceDuration = '4' | '5' | '8' | '10' | '12';
export type SeedanceState = 'waiting' | 'queuing' | 'generating' | 'success' | 'fail';

// Seedance 2.0 input mode
export type Seedance2InputMode = 'text' | 'first-frame' | 'first-last-frame' | 'multimodal';

export interface SeedanceMedia {
  _id: string;
  taskId: string;
  generationType: SeedanceGenerationType;
  model: string;
  prompt: string;
  imageUrl?: string;
  aspectRatio?: SeedanceAspectRatio;
  resolution?: SeedanceResolution;
  // 1.5-pro uses string duration; 2.0 uses integer (4-15)
  duration?: SeedanceDuration | number;
  cameraFixed?: boolean;
  inputUrls?: string[];
  fixedLens?: boolean | null;
  generateAudio?: boolean | null;
  seed?: number;
  enableSafetyChecker?: boolean;
  // Seedance 2.0 specific fields
  firstFrameUrl?: string;
  lastFrameUrl?: string;
  referenceImageUrls?: string[];
  referenceVideoUrls?: string[];
  referenceAudioUrls?: string[];
  webSearch?: boolean;
  nsfwChecker?: boolean;
  // Seedance 2.5 specific fields
  outputFormat?: SeedanceOutputFormat;
  returnLastFrame?: boolean;
  state: SeedanceState;
  resultUrls: string[];
  createTime?: string;
  completeTime?: string;
  costTime?: number;
  failCode?: string;
  failMsg?: string;
  param?: string;
  resultJson?: string;
  workspaceId: string;
  userId?: string;
  assetUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSeedanceTextToVideoRequest {
  prompt: string;
  aspectRatio?: SeedanceAspectRatio;
  resolution?: SeedanceResolution;
  duration?: SeedanceDuration;
  cameraFixed?: boolean;
  seed?: number;
  enableSafetyChecker?: boolean;
  workspaceId: string;
  userId?: string;
}

export interface CreateSeedanceImageToVideoRequest {
  prompt: string;
  imageUrl: string;
  resolution?: SeedanceResolution;
  duration?: SeedanceDuration;
  cameraFixed?: boolean;
  seed?: number;
  enableSafetyChecker?: boolean;
  workspaceId: string;
  userId?: string;
}

export interface CreateSeedance15ProRequest {
  prompt: string;
  inputUrls?: string[];
  aspectRatio?: SeedanceAspectRatio;
  resolution?: SeedanceResolution;
  duration?: '4' | '8' | '12';
  fixedLens?: boolean;
  generateAudio?: boolean;
  workspaceId: string;
  userId?: string;
}

export interface CreateSeedance2Request {
  // Required
  prompt: string;
  workspaceId: string;

  // Optional - user identity
  userId?: string;

  // Optional - generation inputs (mutually exclusive scenarios)
  // Scenario A/B: Image-to-Video
  firstFrameUrl?: string;  // URL or "asset://{assetId}"
  lastFrameUrl?: string;   // URL or "asset://{assetId}"
  // Scenario C: Multimodal Reference-to-Video
  referenceImageUrls?: string[];   // max 9 (combined first+last+reference <= 9)
  referenceVideoUrls?: string[];   // max 3, total <= 15s
  referenceAudioUrls?: string[];   // max 3, total <= 15s

  // Optional - generation settings
  generateAudio?: boolean;                      // default true
  resolution?: '480p' | '720p';                 // default '720p'
  aspectRatio?: '1:1' | '4:3' | '3:4' | '16:9' | '9:16' | '21:9' | 'adaptive'; // default '16:9'
  duration?: number;                            // integer 4-15, default 5
  webSearch?: boolean;                          // default false
  nsfwChecker?: boolean;                        // default false
}

export interface CreateSeedance2FastRequest {
  // Required
  prompt: string;
  workspaceId: string;

  // Optional - user identity
  userId?: string;

  // Optional - multimodal references (no first/last frame in fast mode)
  referenceImageUrls?: string[];
  referenceVideoUrls?: string[];   // total <= 15s, <= 50MB each
  referenceAudioUrls?: string[];   // total <= 15s, <= 15MB each

  // Optional - generation settings
  generateAudio?: boolean;                      // default true
  resolution?: '480p' | '720p';                 // default '720p'
  aspectRatio?: '16:9' | '4:3' | '1:1' | '3:4' | '9:16' | '21:9'; // default '16:9' (no 'adaptive')
  duration?: number;                            // integer 4-15, default 15
  webSearch?: boolean;                          // default false
  nsfwChecker?: boolean;                        // default true
}

export interface CreateSeedance25Request {
  // Required
  prompt: string;
  workspaceId: string;

  // Optional - user identity
  userId?: string;

  // Optional - generation inputs (mutually exclusive scenarios)
  // Scenario A/B: Image-to-Video
  firstFrameUrl?: string;  // URL or "asset://{assetId}"
  lastFrameUrl?: string;   // URL or "asset://{assetId}"
  // Scenario C: Multimodal Reference-to-Video
  referenceImageUrls?: string[];   // max 9 (combined first+last+reference <= 9)
  referenceVideoUrls?: string[];
  referenceAudioUrls?: string[];

  // Optional - generation settings
  generateAudio?: boolean;                      // default true
  returnLastFrame?: boolean;                     // default false
  resolution?: '480p' | '720p' | '1080p';        // default '720p'
  aspectRatio?: '1:1' | '4:3' | '3:4' | '16:9' | '9:16' | '21:9' | 'adaptive'; // default '9:16'
  duration?: number;                            // seconds, native up to 30, default 5
  outputFormat?: SeedanceOutputFormat;           // default 'mp4'
  webSearch?: boolean;                          // default false
  nsfwChecker?: boolean;                        // default false
}

export interface SeedanceResponse {
  success: boolean;
  message: string;
  data: {
    taskId: string;
    seedanceMedia: SeedanceMedia;
  };
}

export interface SeedanceTaskResponse {
  success: boolean;
  data: SeedanceMedia;
}

export interface SeedanceTasksResponse {
  success: boolean;
  data: SeedanceMedia[];
}

// ============================================
// Nano Banana (kie.ai Nano Banana 2 - prompt to image) Types
// ============================================

export type NanoBananaAspectRatio = '1:1'|'1:4'|'1:8'|'2:3'|'3:2'|'3:4'|'4:1'|'4:3'|'4:5'|'5:4'|'8:1'|'9:16'|'16:9'|'21:9'|'auto';
export type NanoBananaResolution = '1K'|'2K'|'4K';
export type NanoBananaOutputFormat = 'jpg'|'png';
export type NanoBananaState = 'pending'|'processing'|'success'|'fail';
export interface NanoBananaMedia { _id:string; taskId:string; model:string; prompt:string; imageInput?:string[]; aspectRatio:NanoBananaAspectRatio; resolution:NanoBananaResolution; outputFormat:NanoBananaOutputFormat; state:NanoBananaState; resultUrls:string[]; createTime?:string; completeTime?:string; costTime?:number; failCode?:string; failMsg?:string; workspaceId:string; userId?:string; assetUrl?:string|null; createdAt:string; updatedAt:string; }
export interface CreateNanoBananaRequest { prompt:string; imageInput?:string[]; aspectRatio?:NanoBananaAspectRatio; resolution?:NanoBananaResolution; outputFormat?:NanoBananaOutputFormat; workspaceId:string; userId?:string; }
export interface NanoBananaResponse { success:boolean; message:string; data:{ taskId:string; nanoBananaMedia:NanoBananaMedia } }
export interface NanoBananaTaskResponse { success:boolean; data:NanoBananaMedia }
export interface NanoBananaTasksResponse { success:boolean; data:NanoBananaMedia[] }

// ============================================
// Veo3 (Google Veo 3.1) Types
// ============================================

export type Veo3Model = 'veo3' | 'veo3_fast';
export type Veo3ExtendModel = 'fast' | 'quality';
export type Veo3GenerationType = 'TEXT_2_VIDEO' | 'FIRST_AND_LAST_FRAMES_2_VIDEO' | 'REFERENCE_2_VIDEO' | 'EXTEND';
export type Veo3AspectRatio = '16:9' | '9:16' | 'Auto';
export type Veo3State = 'pending' | 'processing' | 'success' | 'fail';
export type Veo3FourKState = 'pending' | 'processing' | 'success' | 'fail' | null;

export interface Veo3Media {
  _id: string;
  taskId: string;
  model: string;
  generationType: Veo3GenerationType;
  extendFromTaskId?: string;
  prompt: string;
  imageUrls: string[];
  aspectRatio: Veo3AspectRatio;
  seeds?: number;
  enableTranslation?: boolean;
  watermark?: string;
  state: Veo3State;
  resultUrls: string[];
  originUrls: string[];
  videoUrl?: string;
  videoUrl1080p?: string;
  videoUrl4k?: string;
  fourKTaskId?: string;
  fourKState: Veo3FourKState;
  thumbnailUrl?: string;
  duration?: number;
  resolution?: string;
  fallbackFlag?: boolean;
  failCode?: string;
  failMsg?: string;
  errorCode?: string;
  errorMessage?: string;
  workspaceId: string;
  userId?: string;
  assetUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateVeo3GenerateRequest {
  prompt: string;
  imageUrls?: string[];
  model?: Veo3Model;
  generationType?: 'TEXT_2_VIDEO' | 'FIRST_AND_LAST_FRAMES_2_VIDEO' | 'REFERENCE_2_VIDEO';
  aspectRatio?: Veo3AspectRatio;
  seeds?: number;
  enableTranslation?: boolean;
  watermark?: string;
  workspaceId: string;
  userId?: string;
}

export interface CreateVeo3ExtendRequest {
  taskId: string;
  prompt: string;
  model?: Veo3ExtendModel;
  seeds?: number;
  watermark?: string;
  workspaceId: string;
  userId?: string;
}

export interface Veo3FourKRequest {
  taskId: string;
  index?: number;
}

export interface Veo3Response {
  success: boolean;
  message: string;
  data: {
    taskId: string;
    veo3Media: Veo3Media;
  };
}

export interface Veo3ExtendResponse {
  success: boolean;
  message: string;
  data: {
    taskId: string;
    extendFromTaskId: string;
    veo3Media: Veo3Media;
  };
}

export interface Veo31080pResponse {
  success: boolean;
  data: {
    resultUrl: string;
  };
}

export interface Veo3FourKResponse {
  success: boolean;
  message: string;
  data: {
    taskId: string;
    resultUrls: string[] | null;
  };
}

export interface Veo3TaskResponse {
  success: boolean;
  data: Veo3Media;
}

export interface Veo3TasksResponse {
  success: boolean;
  data: Veo3Media[];
}

// ============================================
// Gemini (Google Gemini Omni 1.1 Flash) Types
// ============================================

export type GeminiAspectRatio = '16:9' | '9:16';
export type GeminiResolution = '360p' | '720p' | '1080p' | '4k';
export type GeminiDuration = '4' | '6' | '8' | '10';
// Same state names as SeedanceMedia (NOT Veo3's pending/processing)
export type GeminiState = 'waiting' | 'queuing' | 'generating' | 'success' | 'fail';

export interface GeminiMedia {
  _id: string;
  taskId: string;
  model: string; // 'google/gemini-omni-flash-1-1'
  prompt: string;
  firstFrameUrl?: string;
  lastFrameUrl?: string;
  imageUrls?: string[];
  videoList?: string;
  duration: GeminiDuration;
  aspectRatio: GeminiAspectRatio;
  resolution: GeminiResolution;
  seed?: number;
  state: GeminiState;
  resultUrls?: string[];
  createTime?: string;
  completeTime?: string;
  costTime?: number;
  failCode?: string;
  failMsg?: string;
  assetUrl?: string | null;
  workspaceId?: string;
  userId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateGeminiGenerateRequest {
  prompt: string;
  firstFrameUrl?: string;
  lastFrameUrl?: string;
  imageUrls?: string[];
  videoList?: string;
  duration?: GeminiDuration;         // default '8'
  aspectRatio?: GeminiAspectRatio;   // default '16:9'
  resolution?: GeminiResolution;     // default '720p'
  seed?: number;
  workspaceId: string;
  userId?: string;
}

export interface GeminiResponse {
  success: boolean;
  message: string;
  data: {
    taskId: string;
    geminiMedia: GeminiMedia;
  };
}

export interface GeminiTaskResponse {
  success: boolean;
  data: GeminiMedia;
}

export interface GeminiTasksResponse {
  success: boolean;
  data: GeminiMedia[];
}

// ============================================
// Wan 3.0 Video Types
// ============================================

export type Wan3Resolution = '480P' | '720P' | '1080P';
export type Wan3AspectRatio = 'adaptive' | '16:9' | '4:3' | '1:1' | '3:4' | '9:16';
// Same state names as SeedanceMedia/GeminiMedia (NOT Veo3's pending/processing)
export type Wan3State = 'waiting' | 'queuing' | 'generating' | 'success' | 'fail';

export interface Wan3Media {
  _id: string;
  taskId: string;
  model: string; // 'wan/3-0-video'
  prompt: string;
  firstFrameUrl?: string;
  lastFrameUrl?: string;
  referenceImageUrls?: string[];
  referenceVideoUrls?: string[];
  referenceAudioUrls?: string[];
  referenceLinkUrls?: string[];
  referenceFileUrls?: string[];
  resolution: Wan3Resolution;
  aspectRatio: Wan3AspectRatio;
  duration: number;
  audio: boolean;
  seed?: number;
  nsfwChecker: boolean;
  state: Wan3State;
  resultUrls?: string[];
  createTime?: string;
  completeTime?: string;
  costTime?: number;
  failCode?: string;
  failMsg?: string;
  assetUrl?: string | null;
  workspaceId?: string;
  userId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateWan3GenerateRequest {
  prompt: string;
  firstFrameUrl?: string;
  lastFrameUrl?: string;
  referenceImageUrls?: string[];   // max 10
  referenceVideoUrls?: string[];   // max 5 clips, 1-15s each, total <= 15s
  referenceAudioUrls?: string[];   // max 5
  referenceLinkUrls?: string[];    // max 1, plain URL text
  referenceFileUrls?: string[];    // max 1, plain URL text
  resolution?: Wan3Resolution;     // default '1080P'
  aspectRatio?: Wan3AspectRatio;   // default 'adaptive'
  duration?: number;               // seconds, -1 (smart duration) to 30, default 5
  audio?: boolean;                 // default true
  seed?: number;
  nsfwChecker?: boolean;           // default true
  workspaceId: string;
  userId?: string;
}

export interface Wan3Response {
  success: boolean;
  message: string;
  data: {
    taskId: string;
    wan3Media: Wan3Media;
  };
}

export interface Wan3TaskResponse {
  success: boolean;
  data: Wan3Media;
}

export interface Wan3TasksResponse {
  success: boolean;
  data: Wan3Media[];
}

// ============================================
// Hetzner Local LLM Engine (self-hosted MiniMax H3) Types
// ============================================

export type HetznerState = 'waiting' | 'generating' | 'success' | 'fail';

export interface HetznerMedia {
  _id: string;
  taskId: string;
  model: string; // 'self-hosted/minimax-h3'
  prompt: string;
  negativePrompt?: string;
  durationS: number;
  resolution: string; // e.g. '1344x768'
  seed?: number;
  state: HetznerState;
  resultUrls?: string[];
  failMsg?: string;
  assetUrl?: string | null;
  workspaceId?: string;
  userId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateHetznerGenerateRequest {
  prompt: string;
  negativePrompt?: string;
  durationS?: number;   // seconds, max 15, default 3
  resolution?: string;  // default '1344x768'
  seed?: number;
  workspaceId?: string;
  userId?: string;
}

export interface HetznerResponse {
  success: boolean;
  message: string;
  data: {
    taskId: string;
    hetznerMedia: HetznerMedia;
  };
}

export interface HetznerTaskResponse {
  success: boolean;
  data: HetznerMedia;
}

export interface HetznerTasksResponse {
  success: boolean;
  data: HetznerMedia[];
}

// ============================================
// Prompt Suggestion (claude-worker, trained on winning ads) Types
// ============================================

export interface PromptSuggestionResult {
  status: 'processing' | 'completed' | 'failed';
  mode?: string;
  preview?: boolean;
  response?: string | null;
  generation?: unknown;
  error?: string;
}

export interface CreatePromptSuggestionRequest {
  userInstructions: string;
  workspaceId?: string;
}

export interface CreatePromptSuggestionResponse {
  success: boolean;
  data: { jobId: string };
}

export interface PromptSuggestionResultResponse {
  success: boolean;
  data: PromptSuggestionResult;
}